import {
  ApolloCache,
  gql,
  NormalizedCacheObject,
  Reference,
} from '@apollo/client';

import { GET_ALL_FILTER_TYPES_QUERY } from './getAllFilterTypesQuery';
import { NO_SITE_FILTER_VALUE } from '../consts';

export const GetMyFindsQueryName = 'GetMyFinds';

export const FIND_CACHE_FRAGMENT = gql`
  fragment FindCacheFields on Find {
    id
    name
    evId
    createdAt
    description
    updatedAt
    foundAt
    material
    era
    type
    note
    localization
    siteId
    groupIds
    latitude
    longitude
    shared
    visibility
    draft
    tags
    image
    images
    amcrId
    amcrData {
      images
    }
  }
`;

export const GET_MY_FINDS_QUERY = gql`
  query ${GetMyFindsQueryName}($filters: Filters) {
    getMyFinds(filters: $filters) {
      id
      name
      evId
      createdAt
      description
      images
      updatedAt
      foundAt
      material
      era
      type
      amcrId
      amcrData {
        images
      }
      note
      siteId
      groupIds
      latitude
      longitude
      shared
      visibility
      draft
      tags
    }
  }
`;

interface FindFilters {
  sites?: string[];
  groups?: string[];
  materials?: string[];
  eras?: string[];
  findTypes?: string[];
  tags?: string[];
  hasImages?: boolean;
  hasAmcrId?: boolean;
}

interface FindListArguments {
  siteId?: string;
  groupId?: string;
  filters?: FindFilters | null;
  pagination?: unknown;
}

interface FilterTypeData {
  getAllFilterTypes?: {
    eras?: { id: string; parentId?: string | null }[];
    findTypes?: { id: string; category?: string | null }[];
  };
}

const parseFieldArguments = (storeFieldName: string): FindListArguments => {
  const argumentsStart = storeFieldName.indexOf('(');

  if (argumentsStart === -1 || !storeFieldName.endsWith(')')) {
    return {};
  }

  try {
    return JSON.parse(storeFieldName.slice(argumentsStart + 1, -1));
  } catch {
    return {};
  }
};

const overlaps = (first?: readonly string[], second?: readonly string[]) =>
  !first?.length || Boolean(second?.some((value) => first.includes(value)));

const normalizeEraId = (value?: string | null) =>
  value?.trim().toLocaleLowerCase();

const matchesFilters = (
  reference: Reference,
  filters: FindFilters | null | undefined,
  filterTypes: FilterTypeData['getAllFilterTypes'],
  readField: <T>(fieldName: string, from: Reference) => T | undefined,
  wasInList: boolean
) => {
  if (!filters) {
    return true;
  }

  const siteId = readField<string | null>('siteId', reference);
  const groupIds = readField<string[]>('groupIds', reference);
  const material = readField<string>('material', reference);
  const era = normalizeEraId(readField<string>('era', reference));
  const findType = readField<string>('type', reference);
  const tags = readField<string[]>('tags', reference);
  const images = readField<string[]>('images', reference);
  const amcrData = readField<unknown>('amcrData', reference);
  const amcrId = readField<string | null>('amcrId', reference);
  const normalizedEraFilters = filters.eras?.map(normalizeEraId);

  const expandedEras = filterTypes?.eras
    ?.filter((option) => option.parentId)
    .filter(
      (option) =>
        normalizedEraFilters?.includes(normalizeEraId(option.id)) ||
        normalizedEraFilters?.includes(normalizeEraId(option.parentId))
    )
    .map((option) => normalizeEraId(option.id));
  const expandedFindTypes = filterTypes?.findTypes
    ?.filter(
      (option) =>
        filters.findTypes?.includes(option.id) ||
        (option.category && filters.findTypes?.includes(option.category))
    )
    .map((option) => option.id);
  const hasImages = Boolean(images?.length || amcrData);
  const matchesAmcrId =
    filters.hasAmcrId == null ||
    (amcrId === undefined
      ? wasInList
      : filters.hasAmcrId === Boolean(amcrId?.trim()));
  const matchesEra =
    !filters.eras?.length ||
    Boolean(normalizedEraFilters?.includes(era) || expandedEras?.includes(era));
  const matchesFindType =
    !filters.findTypes?.length ||
    Boolean(
      filters.findTypes.includes(findType || '') ||
        expandedFindTypes?.includes(findType)
    );
  const matchesTags =
    !filters.tags?.length ||
    (tags == null ? wasInList : overlaps(filters.tags, tags));
  const matchesSite =
    !filters.sites?.length ||
    (siteId === null
      ? filters.sites.includes(NO_SITE_FILTER_VALUE)
      : typeof siteId === 'string' && filters.sites.includes(siteId));

  return (
    matchesSite &&
    overlaps(filters.groups, groupIds) &&
    overlaps(filters.materials, material ? [material] : []) &&
    matchesEra &&
    matchesFindType &&
    matchesTags &&
    matchesAmcrId &&
    (filters.hasImages == null || filters.hasImages === hasImages)
  );
};

export const reconcileFindsInCache = (
  cache: ApolloCache<unknown>,
  findIds: readonly string[]
) => {
  const targetIds = new Set(findIds);
  const references = findIds
    .map((id) => cache.identify({ __typename: 'Find', id }))
    .filter((id): id is string => Boolean(id))
    .map((id) => ({ __ref: id }));
  const filterTypes = cache.readQuery<FilterTypeData>({
    query: GET_ALL_FILTER_TYPES_QUERY,
  })?.getAllFilterTypes;

  const updateList = (
    existing: readonly Reference[],
    storeFieldName: string,
    readField: <T>(fieldName: string, from: Reference) => T | undefined,
    belongsToList: (reference: Reference, args: FindListArguments) => boolean
  ) => {
    const args = parseFieldArguments(storeFieldName);
    const existingTargetIds = new Set(
      existing
        .map((reference) => readField<string>('id', reference))
        .filter((id): id is string => Boolean(id && targetIds.has(id)))
    );
    const remaining = existing.filter((reference) => {
      const id = readField<string>('id', reference);

      return !id || !targetIds.has(id);
    });
    const matching = references.filter((reference) => {
      const id = readField<string>('id', reference);

      if (args.pagination != null && !existingTargetIds.has(id || '')) {
        return false;
      }

      return (
        belongsToList(reference, args) &&
        matchesFilters(
          reference,
          args.filters,
          filterTypes,
          readField,
          existingTargetIds.has(id || '')
        )
      );
    });

    return [...matching, ...remaining];
  };

  cache.modify({
    id: 'ROOT_QUERY',
    fields: {
      getMyFinds(existing: readonly Reference[] = [], details) {
        return updateList(
          existing,
          details.storeFieldName,
          details.readField,
          () => true
        );
      },
      getMyMapFinds(existing: readonly Reference[] = [], details) {
        return updateList(
          existing,
          details.storeFieldName,
          details.readField,
          () => true
        );
      },
      getSiteFinds(existing: readonly Reference[] = [], details) {
        return updateList(
          existing,
          details.storeFieldName,
          details.readField,
          (reference, args) =>
            details.readField<string>('siteId', reference) === args.siteId
        );
      },
      getFindGroupFinds(existing: readonly Reference[] = [], details) {
        return updateList(
          existing,
          details.storeFieldName,
          details.readField,
          (reference, args) =>
            details
              .readField<string[]>('groupIds', reference)
              ?.includes(args.groupId || '') ?? false
        );
      },
    },
  });
};

export const removeFindsFromCache = (
  cache: ApolloCache<unknown>,
  findIds: readonly string[]
) => {
  findIds.forEach((id) => {
    const cacheId = cache.identify({ __typename: 'Find', id });

    if (cacheId) {
      cache.evict({ id: cacheId });
    }
  });
  cache.gc();
};

export const updateSiteFindsInCache = (
  cache: ApolloCache<unknown>,
  sourceSiteId: string,
  targetSiteId: string | null,
  deleteFinds: boolean
) => {
  const records = cache.extract() as NormalizedCacheObject;
  const affectedFindIds = Object.values(records)
    .filter(
      (record) =>
        record?.__typename === 'Find' && record.siteId === sourceSiteId
    )
    .map((record) => record?.id)
    .filter((id): id is string => typeof id === 'string');

  if (deleteFinds) {
    removeFindsFromCache(cache, affectedFindIds);

    return;
  }

  affectedFindIds.forEach((id) => {
    const cacheId = cache.identify({ __typename: 'Find', id });

    if (cacheId) {
      cache.modify({
        id: cacheId,
        fields: {
          siteId: () => targetSiteId,
          updatedAt: () => new Date().toISOString(),
        },
      });
    }
  });
  reconcileFindsInCache(cache, affectedFindIds);
};
